
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Theme = 'light' | 'dark';

export const useThemePreference = () => {
  const queryClient = useQueryClient();
  const [currentTheme, setCurrentTheme] = useState<Theme>('light');
  
  // Get user's theme preference
  const { data: userTheme, isLoading } = useQuery({
    queryKey: ['theme-preference'],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return null;
        
        const { data, error } = await supabase
          .from('profiles')
          .select('theme_preference')
          .eq('id', user.id)
          .single();
          
        if (error) {
          console.error("Error fetching theme preference:", error);
          return null;
        }
        
        return (data?.theme_preference as Theme) || 'light';
      } catch (error) {
        console.error("Error in theme preference fetch:", error);
        return null;
      }
    },
    staleTime: 60000 // 1 minute
  });
  
  // Update theme preference
  const updateTheme = useMutation({
    mutationFn: async (newTheme: Theme) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");
      
      const { error } = await supabase
        .from('profiles')
        .update({ theme_preference: newTheme })
        .eq('id', user.id);
        
      if (error) throw error;
      return newTheme;
    },
    onSuccess: (newTheme) => {
      queryClient.invalidateQueries({ queryKey: ['theme-preference'] });
      applyTheme(newTheme);
    },
    onError: (error) => {
      console.error("Error updating theme preference:", error);
    }
  });
  
  const toggleTheme = () => {
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    updateTheme.mutate(newTheme);
  };
  
  // Apply theme to document
  const applyTheme = (theme: Theme) => {
    setCurrentTheme(theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };
  
  // Initial setup
  useEffect(() => {
    if (userTheme && !isLoading) {
      applyTheme(userTheme);
    }
  }, [userTheme, isLoading]);
  
  return { 
    theme: currentTheme,
    isLoading,
    toggleTheme,
    updateTheme: (theme: Theme) => updateTheme.mutate(theme)
  };
};
